import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { IGlobalReducerState } from 'Store/Base/interface/IGlobalReducerState';
import { useQueryBillingSplit } from 'Hooks/faturamento/useQueryBillingSplit';
import { useRateioScope } from 'Hooks/faturamento/RateioScope';
import { useMutationRateio } from 'Hooks/faturamento/useMutationRateio';
import { useMutationSpe } from 'Hooks/faturamento/useMutationSpe';
import { useMutationResendTerms } from 'Hooks/faturamento/useMutationResendTerms';
import { ITermsAcceptanceResponse } from 'Data/interfaces/Faturamento/ITermsAcceptance';
import { RateioModeEnum } from 'Data/interfaces/Faturamento/IRateio';
import { ISpeRequest, ISpeResponse } from 'Data/interfaces/Faturamento/ISpe';
import {
  distributeUniformWeights,
  formatWeight,
  parseWeight,
  round2,
  sumWeights,
  weightToAmount,
} from 'Utils/RateioUtils';

export interface IRateioRow {
  speId: string;
  name: string;
  document: string;
  // Texto cru do campo, para o usuário poder digitar livremente.
  input: string;
  weight: number | null;
  amount?: number;
  isRoundingAdjusted: boolean;
  // Ciência do representante legal nos Termos de Uso.
  terms?: ITermsAcceptanceResponse | null;
}

export const useRateio = () => {
  const scope = useRateioScope();
  const { billingSplit, isLoading } = useQueryBillingSplit();
  const { updateRateio, isLoading: isSaving } = useMutationRateio();
  const {
    addSpe,
    updateSpe,
    removeSpe,
    isSaving: isSavingSpe,
    isRemoving,
  } = useMutationSpe();
  const { resendTerms, resendingSpeId } = useMutationResendTerms();

  // undefined = fechado; null = cadastro novo; objeto = edição daquela SPE.
  const [speBeingEdited, setSpeBeingEdited] = useState<
    ISpeResponse | null | undefined
  >(undefined);
  const [speToRemove, setSpeToRemove] = useState<ISpeResponse | undefined>();

  // No /adm o preço é o do tenant em escopo, não o do usuário logado (Coordly).
  const userPlanPrice = useSelector((state: IGlobalReducerState) => {
    const storeItem = state.auth.userInfo?.UserStoreItem;
    return storeItem?.Price || storeItem?.StorePlan?.Price;
  });
  const planPrice = scope.tenantId ? scope.planPrice : userPlanPrice;

  const [mode, setMode] = useState<RateioModeEnum>(RateioModeEnum.Uniform);
  const [inputs, setInputs] = useState<Record<string, string>>({});

  // Semeia o formulário a partir do que veio do servidor, uma vez.
  useEffect(() => {
    if (!billingSplit?.Rateio) return;

    setMode(billingSplit.Rateio.Mode);
    setInputs(
      billingSplit.Rateio.Shares.reduce<Record<string, string>>((acc, share) => {
        acc[share.SpeId] = formatWeight(share.Weight);
        return acc;
      }, {})
    );
  }, [billingSplit?.Rateio]);

  const spes = useMemo(() => billingSplit?.Spes ?? [], [billingSplit?.Spes]);

  // Em divisão igual os pesos são calculados e o campo fica somente leitura.
  const uniformShares = useMemo(
    () => distributeUniformWeights(spes.length),
    [spes.length]
  );

  const rows = useMemo<IRateioRow[]>(() => {
    return spes.map((spe, index) => {
      const isUniform = mode === RateioModeEnum.Uniform;
      const uniform = uniformShares[index];
      const weight = isUniform ? uniform?.weight ?? 0 : parseWeight(inputs[spe.SpeId] ?? '');

      return {
        speId: spe.SpeId,
        name: spe.CompanyName,
        document: spe.CNPJ,
        input: isUniform
          ? formatWeight(uniform?.weight ?? 0)
          : inputs[spe.SpeId] ?? '',
        weight,
        amount: weight === null ? undefined : weightToAmount(weight, planPrice),
        isRoundingAdjusted: isUniform && (uniform?.isRoundingAdjusted ?? false),
        terms: spe.TermsAcceptance,
      };
    });
  }, [spes, mode, uniformShares, inputs, planPrice]);

  const total = useMemo(
    () => sumWeights(rows.map((row) => row.weight ?? 0)),
    [rows]
  );

  const hasInvalidInput = useMemo(
    () => mode === RateioModeEnum.Custom && rows.some((row) => row.weight === null),
    [mode, rows]
  );

  const difference = useMemo(() => round2(100 - total), [total]);
  const isComplete = total === 100 && !hasInvalidInput;

  const totalAmount = useMemo(
    () => weightToAmount(total, planPrice),
    [total, planPrice]
  );

  const differenceAmount = useMemo(
    () => weightToAmount(Math.abs(difference), planPrice),
    [difference, planPrice]
  );

  const onModeChange = (nextMode: RateioModeEnum) => {
    setMode(nextMode);

    // Ao sair da divisão igual, os campos herdam os pesos calculados em vez de
    // abrirem vazios — o usuário ajusta a partir de algo válido.
    if (nextMode === RateioModeEnum.Custom) {
      setInputs(
        spes.reduce<Record<string, string>>((acc, spe, index) => {
          acc[spe.SpeId] = formatWeight(uniformShares[index]?.weight ?? 0);
          return acc;
        }, {})
      );
    }
  };

  const onWeightChange = (speId: string, value: string) => {
    setInputs((current) => ({ ...current, [speId]: value }));
  };

  const onSave = () => {
    if (!isComplete) return;

    updateRateio({
      mode,
      shares: rows.map((row) => ({ speId: row.speId, weight: row.weight ?? 0 })),
    });
  };

  const onAddSpe = () => setSpeBeingEdited(null);
  const onEditSpe = (speId: string) => {
    const spe = spes.find((item) => item.SpeId === speId);
    if (spe) setSpeBeingEdited(spe);
  };
  const onCloseSpeForm = () => setSpeBeingEdited(undefined);

  // Só fecha o formulário no sucesso: se a chamada falhar, o usuário não perde o
  // que digitou (o toast de erro vem do useMutationSpe).
  const onSubmitSpe = (spe: ISpeRequest) => {
    if (speBeingEdited) {
      updateSpe(
        { speId: speBeingEdited.SpeId, spe },
        { onSuccess: onCloseSpeForm }
      );
      return;
    }

    addSpe(spe, { onSuccess: onCloseSpeForm });
  };

  const onRequestRemoveSpe = (speId: string) => {
    const spe = spes.find((item) => item.SpeId === speId);
    if (spe) setSpeToRemove(spe);
  };
  const onCancelRemoveSpe = () => setSpeToRemove(undefined);

  const onConfirmRemoveSpe = () => {
    if (!speToRemove) return;

    removeSpe(speToRemove.SpeId, {
      onSuccess: () => {
        // Limpa o peso digitado da SPE removida para ele não reaparecer se uma
        // nova SPE vier a receber o mesmo id.
        setInputs((current) => {
          const { [speToRemove.SpeId]: _removed, ...rest } = current;
          return rest;
        });
        setSpeToRemove(undefined);
      },
    });
  };

  return {
    rows,
    mode,
    total,
    totalAmount,
    difference,
    differenceAmount,
    isComplete,
    hasInvalidInput,
    isLoading,
    isSaving,
    planPrice,
    hasSpes: spes.length > 0,
    headquartersTerms: billingSplit?.HeadquartersTermsAcceptance,
    resendingSpeId,
    // speId ausente = matriz.
    onResendTerms: (speId?: string) => resendTerms(speId),
    isSpeFormOpen: speBeingEdited !== undefined,
    speBeingEdited: speBeingEdited ?? undefined,
    isSavingSpe,
    speToRemove,
    isRemoving,
    onModeChange,
    onWeightChange,
    onSave,
    onAddSpe,
    onEditSpe,
    onCloseSpeForm,
    onSubmitSpe,
    onRequestRemoveSpe,
    onCancelRemoveSpe,
    onConfirmRemoveSpe,
  };
};
